import * as Colors from '@brightlayer-ui/colors';
import { Cancel, Done } from '@mui/icons-material';
import {
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../lib/AlertContext';
import { OrganizationService } from '../../services/organization/organization.service';
import { Data, EnhancedTableProps, HeadCell, Status, TableComponentProps } from './types';

const styles = {
    doneIcon: {
        fontSize: 56,
        color: Colors.green[500],
    },
    cancelIcon: {
        fontSize: 56,
        color: Colors.red[500],
    },
    acceptButton: {
        minWidth: 120,
        height: 40,
        fontSize: 16,
    },
    rejectButton: {
        ml: 1,
        backgroundColor: Colors.red[500],
        color: Colors.white[50],
        minWidth: 120,
        height: 40,
        fontSize: 16,
        '&:hover': {
            backgroundColor: Colors.red[300],
        },
    },
};

const EnhancedTableHead: React.FC<EnhancedTableProps & { status: Status }> = ({
    onRequestSort,
    status,
    order,
    orderBy,
}) => {
    const { t } = useTranslation();

    const createSortHandler =
        (property: keyof Data): ((event: React.MouseEvent<unknown>) => void) =>
        (event: React.MouseEvent<unknown>): void => {
            onRequestSort(event, property);
        };

    const headCells: HeadCell[] = [
        {
            id: t('ADMIN_DASHBOARD.ORGANIZATION_NAME_ID'),
            numeric: false,
            disablePadding: true,
            label: t('ADMIN_DASHBOARD.ORGANIZATION_NAME'),
        },
        {
            id: t('ADMIN_DASHBOARD.REQUESTER_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.REQUESTER'),
        },
        { id: t('ADMIN_DASHBOARD.EMAIL_ID'), numeric: false, disablePadding: false, label: t('ADMIN_DASHBOARD.EMAIL') },
        {
            id: t('ADMIN_DASHBOARD.DATE_REQUESTED_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.DATE_REQUESTED'),
        },
        {
            id: t('ADMIN_DASHBOARD.APPROVED_BY_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.APPROVED_BY'),
        },
        {
            id: t('ADMIN_DASHBOARD.APPROVED_DATE_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.APPROVED_DATE'),
        },
        {
            id: t('ADMIN_DASHBOARD.REJECTED_BY_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.REJECTED_BY'),
        },
        {
            id: t('ADMIN_DASHBOARD.REJECTED_DATE_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.REJECTED_DATE'),
        },
        {
            id: t('ADMIN_DASHBOARD.ACTIONS_ID'),
            numeric: false,
            disablePadding: false,
            label: t('ADMIN_DASHBOARD.ACTIONS'),
        },
    ];

    // Dynamically filter headCells based on the status
    const filteredHeadCells = headCells.filter((headCell) => {
        if (status === Status.REQUESTED) {
            return [
                t('ADMIN_DASHBOARD.ORGANIZATION_NAME_ID'),
                t('ADMIN_DASHBOARD.REQUESTER_ID'),
                t('ADMIN_DASHBOARD.EMAIL_ID'),
                t('ADMIN_DASHBOARD.DATE_REQUESTED_ID'),
                t('ADMIN_DASHBOARD.ACTIONS_ID'),
            ].includes(headCell.id);
        } else if (status === Status.APPROVED || status === Status.CLAIMED) {
            return [
                t('ADMIN_DASHBOARD.ORGANIZATION_NAME_ID'),
                t('ADMIN_DASHBOARD.REQUESTER_ID'),
                t('ADMIN_DASHBOARD.EMAIL_ID'),
                t('ADMIN_DASHBOARD.APPROVED_BY_ID'),
                t('ADMIN_DASHBOARD.APPROVED_DATE_ID'),
            ].includes(headCell.id);
        } else if (status === Status.REJECTED) {
            return [
                t('ADMIN_DASHBOARD.ORGANIZATION_NAME_ID'),
                t('ADMIN_DASHBOARD.REQUESTER_ID'),
                t('ADMIN_DASHBOARD.EMAIL_ID'),
                t('ADMIN_DASHBOARD.REJECTED_BY_ID'),
                t('ADMIN_DASHBOARD.REJECTED_DATE_ID'),
            ].includes(headCell.id);
        }
        return false;
    });

    return (
        <TableHead>
            <TableRow>
                {filteredHeadCells.map((headCell) => {
                    let dataKey: keyof Data | undefined;
                    switch (headCell.id) {
                        case t('ADMIN_DASHBOARD.ORGANIZATION_NAME_ID'):
                            dataKey = 'organizationName';
                            break;
                        case t('ADMIN_DASHBOARD.REQUESTER_ID'):
                            dataKey = 'requesterName';
                            break;
                        case t('ADMIN_DASHBOARD.EMAIL_ID'):
                            dataKey = 'requesterEmail';
                            break;
                        case t('ADMIN_DASHBOARD.DATE_REQUESTED_ID'):
                            dataKey = 'requestedDate';
                            break;
                        case t('ADMIN_DASHBOARD.APPROVED_BY_ID'):
                            dataKey = 'reviewerName';
                            break;
                        case t('ADMIN_DASHBOARD.APPROVED_DATE_ID'):
                            dataKey = 'reviewedDate';
                            break;
                        case t('ADMIN_DASHBOARD.REJECTED_BY_ID'):
                            dataKey = 'reviewerName';
                            break;
                        case t('ADMIN_DASHBOARD.REJECTED_DATE_ID'):
                            dataKey = 'reviewedDate';
                            break;
                        default:
                            dataKey = undefined;
                    }

                    const isSortable = [
                        t('ADMIN_DASHBOARD.ORGANIZATION_NAME_ID'),
                        t('ADMIN_DASHBOARD.REQUESTER_ID'),
                        t('ADMIN_DASHBOARD.DATE_REQUESTED_ID'),
                        t('ADMIN_DASHBOARD.APPROVED_BY_ID'),
                        t('ADMIN_DASHBOARD.APPROVED_DATE_ID'),
                        t('ADMIN_DASHBOARD.REJECTED_BY_ID'),
                        t('ADMIN_DASHBOARD.REJECTED_DATE_ID'),
                    ].includes(headCell.id);

                    return (
                        <TableCell key={headCell.id} align={headCell.numeric ? 'right' : 'left'}>
                            {isSortable && dataKey ? (
                                <TableSortLabel
                                    data-testid={`${headCell.id}-sort-button`}
                                    active={orderBy === dataKey}
                                    direction={orderBy === dataKey ? order : 'asc'}
                                    onClick={createSortHandler(dataKey)}
                                >
                                    {headCell.label}
                                </TableSortLabel>
                            ) : (
                                headCell.label
                            )}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
};

const TableComponent: React.FC<TableComponentProps> = ({
    rows,
    order,
    orderBy,
    onRequestSort,
    onSelectAllClick,
    numSelected,
    page,
    rowsPerPage,
    emptyRows,
    onPageChange,
    onRowsPerPageChange,
    onStatusChange,
    status,
    comparator,
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { showErrorToUser, showInfoAlertToUser } = useAlert();

    // Use status prop if provided, otherwise fallback to first row or REQUESTED
    const currentStatus = status ?? rows[0]?.status ?? Status.REQUESTED;

    // Filter rows based on the current status
    const filteredRows =
        currentStatus === Status.APPROVED || currentStatus === Status.CLAIMED
            ? rows.filter((row) => row.status === Status.APPROVED || row.status === Status.CLAIMED)
            : rows.filter((row) => row.status === currentStatus);

    // Sort filteredRows before paginating
    const sortedRows = comparator ? [...filteredRows].sort(comparator) : filteredRows;

    // Paginate after sorting
    const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const renderFailedToProcessRequestError = (): void => {
        showErrorToUser({
            errorTitle: t('ADMIN_DASHBOARD.FAILED_GENERIC_TITLE'),
            errorDescription: t('ADMIN_DASHBOARD.FAILED_GENERIC_MESSAGE'),
            confirmText: t('COMMON.TRY_AGAIN'),
            cancelText: t('COMMON.CANCEL'),
        });
    };

    const successInformationMessage = (reviewStatus: Status.APPROVED | Status.REJECTED): void => {
        showInfoAlertToUser({
            title:
                reviewStatus === Status.APPROVED
                    ? t('ADMIN_DASHBOARD.REQUEST_APPROVED')
                    : t('ADMIN_DASHBOARD.REQUEST_REJECTED'),
            description:
                reviewStatus === Status.APPROVED
                    ? t('ADMIN_DASHBOARD.REQUEST_APPROVED_MESSAGE')
                    : t('ADMIN_DASHBOARD.REQUEST_REJECTED_MESSAGE'),
            icon:
                reviewStatus === Status.APPROVED ? (
                    <Done style={styles.doneIcon} />
                ) : (
                    <Cancel style={styles.cancelIcon} />
                ),
        });
    };

    const reviewOrgRequest = async (reviewStatus: Status.APPROVED | Status.REJECTED, orgId: string): Promise<void> => {
        try {
            setIsLoading(true);
            await OrganizationService.reviewOrganization({ status: reviewStatus, orgId });
            successInformationMessage(reviewStatus);
            if (onStatusChange) {
                onStatusChange(reviewStatus, orgId);
            }
        } catch {
            renderFailedToProcessRequestError();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <TableContainer>
                <Table>
                    <EnhancedTableHead
                        numSelected={numSelected}
                        order={order}
                        orderBy={orderBy}
                        onSelectAllClick={onSelectAllClick}
                        onRequestSort={onRequestSort}
                        rowCount={filteredRows.length}
                        status={currentStatus}
                    />
                    {!isLoading ? (
                        <TableBody>
                            {paginatedRows.map((row) => (
                                <TableRow hover tabIndex={-1} key={row.organizationId}>
                                    <TableCell>{row.organizationName}</TableCell>
                                    <TableCell>{row.requesterName}</TableCell>
                                    <TableCell>{row.requesterEmail}</TableCell>
                                    {row.status === Status.REQUESTED && (
                                        <>
                                            <TableCell>
                                                {row.requestedDate
                                                    ? new Date(row.requestedDate).toLocaleDateString()
                                                    : ''}
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    id={`${row.organizationId}-accept-button`}
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<Done />}
                                                    onClick={(): void => {
                                                        void reviewOrgRequest(Status.APPROVED, row.organizationId);
                                                    }}
                                                    sx={styles.acceptButton}
                                                >
                                                    {isLoading ? (
                                                        <CircularProgress
                                                            sx={{ color: 'white' }}
                                                            size={20}
                                                            variant={'indeterminate'}
                                                        />
                                                    ) : (
                                                        t('ADMIN_DASHBOARD.ACCEPT')
                                                    )}
                                                </Button>
                                                <Button
                                                    id={`${row.organizationId}-reject-button`}
                                                    variant="contained"
                                                    color="error"
                                                    sx={styles.rejectButton}
                                                    startIcon={<Cancel />}
                                                    onClick={(): void => {
                                                        void reviewOrgRequest(Status.REJECTED, row.organizationId);
                                                    }}
                                                >
                                                    {isLoading ? (
                                                        <CircularProgress
                                                            sx={{ color: 'white' }}
                                                            size={20}
                                                            variant={'indeterminate'}
                                                        />
                                                    ) : (
                                                        t('ADMIN_DASHBOARD.REJECT')
                                                    )}
                                                </Button>
                                            </TableCell>
                                        </>
                                    )}
                                    {(row.status === Status.APPROVED || row.status === Status.CLAIMED) && (
                                        <>
                                            <TableCell>{row.reviewerName}</TableCell>
                                            <TableCell>
                                                {row.reviewedDate
                                                    ? new Date(row.reviewedDate).toLocaleDateString()
                                                    : ''}
                                            </TableCell>
                                        </>
                                    )}
                                    {row.status === Status.REJECTED && (
                                        <>
                                            <TableCell>{row.reviewerName}</TableCell>
                                            <TableCell>
                                                {row.reviewedDate
                                                    ? new Date(row.reviewedDate).toLocaleDateString()
                                                    : ''}
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        <TableRow>
                            <TableCell colSpan={10} align="center" sx={{ height: 300 }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: 250,
                                    }}
                                >
                                    <CircularProgress sx={{ color: 'gray' }} size={60} variant="indeterminate" />
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </Table>
            </TableContainer>
            <TablePagination
                data-testid={'table-pagination'}
                rowsPerPageOptions={((): number[] => {
                    const total = filteredRows.length;
                    const options: number[] = [];
                    for (let i = 5; i <= total + 5; i += 5) {
                        options.push(i);
                        if (i >= total) break;
                    }
                    return options;
                })()}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </>
    );
};

export default TableComponent;
